import { clsx } from 'clsx';

/**
 * Row measures shared by the side navigation's entries and the account
 * trigger in its footer. The rail is 56px wide with 10px padding (px-l), so a
 * row is 36px wide when folded; its 8px inset (px-m) and 20px icon box put
 * every icon on the rail's centre line. Rows are 34px tall with 2px between them.
 */

const NAV_FADE = 'transition-opacity ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none';

// Labels fade out quickly, before the folding row clips them, and in over the widening.
export const navFadeClassName = (expanded: boolean): string =>
    clsx(NAV_FADE, expanded ? 'opacity-100 duration-[400ms]' : 'opacity-0 duration-150');

const NAV_ROW_BASE =
    'group/nav-row relative flex w-full shrink-0 cursor-pointer select-none items-center gap-l overflow-hidden rounded-md border-0 px-m py-0 text-left font-sans no-underline outline-none focus-visible:outline-2! focus-visible:-outline-offset-2! focus-visible:outline-solid focus-visible:outline-focus-ring';

// The current page is pressed into the rail, the same recessed surface as an active tertiary Button.
export const navRowClassName = (active: boolean): string =>
    clsx(
        NAV_ROW_BASE,
        'h-[34px] transition-[background-color,color,box-shadow] duration-150 ease-out motion-reduce:transition-none',
        active
            ? 'surface-recessed-pocket-pressed text-text-content-strong'
            : 'bg-transparent text-text-muted hover:bg-surface-interactive hover:text-text-content-strong',
    );

// A child row narrows from 34px to 16px on the folded rail, where only its
// stripe shows (side-navigation.css), so a parent's pages stack under its icon.
export const navChildRowClassName = (active: boolean, expanded: boolean): string =>
    clsx(
        NAV_ROW_BASE,
        'transition-[height,background-color,color,box-shadow] duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
        expanded ? 'h-[34px]' : 'h-4',
        active && expanded && 'surface-recessed-pocket-pressed',
        active ? 'text-text-content-strong' : 'text-text-muted hover:text-text-content-strong',
        !active && expanded && 'hover:bg-surface-interactive',
        !(active && expanded) && 'bg-transparent',
    );

// Icon lines drawn at 1.5 rather than the set's 2, a lighter rail beside the labels.
export const navRowIconClassName = (active: boolean): string =>
    clsx(
        'relative flex size-5 shrink-0 items-center justify-center transition-colors duration-150 motion-reduce:transition-none',
        '[&_svg_*]:[stroke-width:1.5]',
        active ? 'text-text-content-strong' : 'text-text-content-faint group-hover/nav-row:text-text-muted',
    );

// A child row sets its label one weight lighter so the parent reads above it.
export const navRowLabelClassName = (expanded: boolean, level: 'child' | 'top' = 'top'): string =>
    clsx(
        'flex min-w-0 flex-1 items-center gap-s whitespace-nowrap text-body-xs',
        level === 'child' ? 'font-normal' : 'font-medium',
        navFadeClassName(expanded),
    );
